
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { TeamMemberData } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface FinalBandingTableManageTeamProps {
  teamMembersData: TeamMemberData[];
  onRowSave: (member: TeamMemberData) => void;
  onRowEdit: (memberId: string) => void;
  onCommentsChange: (memberId: string, comments: string) => void;
  isReleasedBanding?: boolean;
  isUnitHead?: boolean;
  currentUser?: any;
}

const FinalBandingTableManageTeam = ({
  teamMembersData,
  onRowSave,
  onRowEdit,
  onCommentsChange,
  isReleasedBanding = false,
  isUnitHead = false,
  currentUser
}: FinalBandingTableManageTeamProps) => {
  const [savingComments, setSavingComments] = useState<Record<string, boolean>>({});
  const [isSavingAllComments, setIsSavingAllComments] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string }>({
    success: false,
    message: ""
  });
  const { activeYear } = usePerformanceYear();

  // Sort team members alphabetically by full_name
  const sortedTeamMembersData = [...teamMembersData].sort((a, b) => 
    a.full_name.localeCompare(b.full_name)
  );

  const handleCommentSave = async (memberId: string, comments: string) => {
    setSavingComments(prev => ({ ...prev, [memberId]: true }));
    
    try {
      const { error } = await supabase
        .from('performance_bands')
        .update({
          supervisor_banding_comments: comments,
          updated_at: new Date().toISOString()
        })
        .eq('employee_id', memberId)
        .eq('year', activeYear);

      if (error) {
        console.error('Error saving supervisor comments:', error);
      }
    } catch (error) {
      console.error('Error saving supervisor comments:', error);
    } finally {
      setSavingComments(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleSaveAllComments = async () => {
    if (!currentUser || isSavingAllComments) return;

    setIsSavingAllComments(true);
    
    try {
      console.log('Starting save all comments for supervisor:', currentUser.id);
      console.log('Active year:', activeYear);
      console.log('Team members data:', sortedTeamMembersData);

      // Step 1: Check existing records for all team members
      const employeeIds = sortedTeamMembersData.map(member => member.id);
      
      const { data: existingRecords, error: fetchError } = await supabase
        .from('performance_bands')
        .select('employee_id, status')
        .in('employee_id', employeeIds)
        .eq('year', activeYear);

      if (fetchError) {
        console.error('Error fetching existing records:', fetchError);
        setSaveResult({
          success: false,
          message: `Error checking existing records: ${fetchError.message}`
        });
        return;
      }

      console.log('Existing records found:', existingRecords);

      // Step 2: Filter out team members who have records with protected status values
      const protectedStatuses = [
        'Final Band Accepted',
        'Band disagreement resolution completed',
        'Final Banding Done',
        'Band disagreement submitted'
      ];

      const protectedEmployeeIds = new Set(
        (existingRecords || [])
          .filter(record => protectedStatuses.includes(record.status))
          .map(record => record.employee_id)
      );

      console.log('Protected employee IDs:', Array.from(protectedEmployeeIds));

      // Step 3: Filter eligible team members (those without protected status records)
      const eligibleMembers = sortedTeamMembersData.filter(member => 
        !protectedEmployeeIds.has(member.id)
      );

      console.log('Eligible members for new records:', eligibleMembers);

      if (eligibleMembers.length === 0) {
        setSaveResult({
          success: true,
          message: 'No new records created. All team members already have finalized performance bands.'
        });
        return;
      }

      // Step 4: Create new records only for eligible team members
      const recordsToInsert = eligibleMembers.map(member => {
        console.log('Processing eligible member:', member.id, member.full_name);
        
        return {
          user_id: member.id,
          employee_id: member.id,
          supervisor_id: currentUser.id,
          year: activeYear,
          team_member_name: member.full_name,
          supervisor_name: currentUser.full_name || currentUser.email || 'Unknown',
          status: 'Allocated Performance Band',
          self_rating: member.averageSelfRating || null,
          manager_rating: member.averageManagerRating || null,
          supervisor_banding_comments: member.supervisorBandingComments || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      });

      console.log('Records to insert:', recordsToInsert);

      // Step 5: Insert new records only (no upsert to avoid updating existing records)
      const { data, error } = await supabase
        .from('performance_bands')
        .insert(recordsToInsert)
        .select();

      console.log('Insert result - data:', data);
      console.log('Insert result - error:', error);

      if (error) {
        console.error('Error inserting new records:', error);
        setSaveResult({
          success: false,
          message: `Error saving comments: ${error.message}`
        });
      } else {
        const skippedCount = sortedTeamMembersData.length - eligibleMembers.length;
        let message = `Comments saved successfully for ${eligibleMembers.length} team members`;
        
        if (skippedCount > 0) {
          message += `. ${skippedCount} team members were skipped as they already have finalized performance bands.`;
        }
        
        setSaveResult({
          success: true,
          message: message
        });
        console.log('All eligible comments saved successfully');
      }

    } catch (error) {
      console.error('Error in handleSaveAllComments:', error);
      setSaveResult({
        success: false,
        message: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSavingAllComments(false);
      setShowResultDialog(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, memberId: string, comments: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCommentSave(memberId, comments);
    }
  };

  const handleBlur = (memberId: string, comments: string) => {
    handleCommentSave(memberId, comments);
  };

  // Helper function to safely get rating values with proper null checks
  const getSafeRating = (member: TeamMemberData, ratingType: 'self' | 'manager'): number => {
    let rating: number | undefined | null = 0;
    
    if (ratingType === 'self') {
      rating = member.averageSelfRating;
    } else {
      rating = member.averageManagerRating;
    }
    
    // Ensure we return a valid number, defaulting to 0 if undefined or null
    return typeof rating === 'number' && !isNaN(rating) ? rating : 0;
  };

  // Check if user is a supervisor (has team members data and currentUser)
  const isSupervisor = currentUser && sortedTeamMembersData.length > 0;

  return (
    <div className="space-y-4">
      <Card className="bg-secondary border-primary border flex flex-col h-[340px]">
        <CardHeader className="flex-shrink-0 pb-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <CardTitle className="text-white">Final Performance Banding</CardTitle>
              {isReleasedBanding && (
                <Badge className="bg-green-500/20 text-green-300 text-[10px] border-green-500/50 px-2 py-1">
                  Performance bands released
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden">
          {sortedTeamMembersData.length > 0 ? (
            <div className="h-full overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-secondary z-10">
                  <TableRow className="border-primary/20 hover:bg-secondary/30">
                    <TableHead className="text-primary text-[10px] w-[3%]">SN</TableHead>
                    <TableHead className="text-primary text-[10px] w-[25%]">Employee ID</TableHead>
                    <TableHead className="text-primary text-[10px] w-[15%]">Team Member Name</TableHead>
                    <TableHead className="text-primary text-[10px] w-[10%]">Self Rating</TableHead>
                    <TableHead className="text-primary text-[10px] w-[10%]">Manager Rating</TableHead>
                    <TableHead className="text-primary text-[10px] w-[45%]">
                      Supervisor Comments
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTeamMembersData.map((member, index) => {
                    const selfRating = getSafeRating(member, 'self');
                    const managerRating = getSafeRating(member, 'manager');
                    
                    return (
                      <TableRow key={member.id} className="border-primary/10 hover:bg-secondary/20">
                        <TableCell className="text-white text-[10px]">{index + 1}</TableCell>
                        <TableCell className="text-white text-[10px]">{member.id}</TableCell>
                        <TableCell className="text-white text-[10px]">
                          <div className="flex flex-col">
                            <span>{member.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-white text-[10px]">
                          {selfRating > 0 ? selfRating.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-white text-[10px]">
                          {managerRating > 0 ? managerRating.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-white text-[10px]">
                          <div className="relative">
                            <textarea 
                              value={member.supervisorBandingComments || ''} 
                              onChange={e => onCommentsChange(member.id, e.target.value)}
                              onKeyDown={e => handleKeyDown(e, member.id, member.supervisorBandingComments || '')}
                              onBlur={() => handleBlur(member.id, member.supervisorBandingComments || '')}
                              className="w-full h-8 text-[10px] border border-primary/20 rounded px-1 py-0.5 text-white resize-none bg-secondary/30"
                              placeholder="Add comments..." 
                            />
                            {savingComments[member.id] && (
                              <div className="absolute top-0 right-1 text-green-400 text-[8px]">
                                Saving...
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">No team members found or no performance data available.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {isSupervisor && (
        <div className="flex justify-center">
          <Button 
            onClick={handleSaveAllComments}
            disabled={isSavingAllComments || isReleasedBanding}
            className="bg-primary hover:bg-primary/80 text-black font-medium"
          >
            {isSavingAllComments ? "Saving Comments..." : "Save Comments"}
          </Button>
        </div>
      )}

      <AlertDialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <AlertDialogContent className="bg-secondary border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className={`text-lg font-semibold ${saveResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {saveResult.success ? 'Success' : 'Error'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white">
              {saveResult.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setShowResultDialog(false)}
              className="bg-primary text-black hover:bg-primary/80"
            >
              Close
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FinalBandingTableManageTeam;
